// client/src/components/Auth/AuthProvider.jsx
import React, { useEffect } from 'react';
import { AuthProvider } from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';
import { useNavigate } from 'react-router-dom';
import { getLogger } from "@/utils/logger";

// oidc-client-ts defaults to sessionStorage; the app previously used Auth0's
// cacheLocation="localstorage" so sessions survive closing the tab/browser.
// Match that here.
const userStore = new WebStorageStateStore({ store: window.localStorage });

const log = getLogger("AuthProvider");


export const AuthProviderWithNavigate = ({ children }) => {
  const navigate = useNavigate();

  // Log Keycloak configuration on load (sanitized for security)
  useEffect(() => {
    log.info(
      `[AuthProvider:useEffect] > [Request]: load config > [Comment]: ${JSON.stringify({
        authority: import.meta.env.VITE_KEYCLOAK_ISSUER_URL ? "configured" : "missing",
        clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ? "configured" : "missing",
        redirectUri: window.location.origin,
      })}`
    );
  }, []);

  // Called once the Authorization Code + PKCE callback has been processed.
  const onSigninCallback = (user) => {
    window.history.replaceState({}, document.title, window.location.pathname);
    navigate(user?.state?.returnTo || window.location.pathname, { replace: true });
  };

  // Ensure all required properties are present
  if (!import.meta.env.VITE_KEYCLOAK_ISSUER_URL || !import.meta.env.VITE_KEYCLOAK_CLIENT_ID) {
    log.error(
      "[AuthProvider:AuthProviderWithNavigate] > [Response]: configuration incomplete"
    );
    return (
      <div className="p-4 bg-red-50 border border-red-300 rounded-md mt-4">
        <h2 className="text-lg font-bold text-red-700">Keycloak Configuration Error</h2>
        <p className="text-red-600">
          Authentication is not properly configured. Please check your environment variables.
        </p>
      </div>
    );
  }

return (
  <AuthProvider
    authority={import.meta.env.VITE_KEYCLOAK_ISSUER_URL}
    client_id={import.meta.env.VITE_KEYCLOAK_CLIENT_ID}
    redirect_uri={window.location.origin}
    scope="openid profile email offline_access"
    automaticSilentRenew={true}
    userStore={userStore}
    onSigninCallback={onSigninCallback}
  >
    {children}
  </AuthProvider>
);
};
